import { useState } from "react";
import Papa from "papaparse";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Upload, Download, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import type { BulkImportResult } from "../lib/api";

interface StudentEntry {
  email?: string;
  username?: string;
  status?: "pending" | "success" | "failed";
  error?: string;
}

interface BulkStudentImportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classroomId: string;
  onComplete?: () => void;
}

export function BulkStudentImport({
  open,
  onOpenChange,
  classroomId,
  onComplete,
}: BulkStudentImportProps) {
  const [students, setStudents] = useState<StudentEntry[]>([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<BulkImportResult[]>([]);
  const [completed, setCompleted] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset state
    setStudents([]);
    setResults([]);
    setCompleted(false);

    // Validate file type
    if (!file.name.endsWith(".csv")) {
      alert("Please upload a CSV file");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const parsed = result.data as Array<{
          email?: string;
          username?: string;
        }>;

        // Validate and normalize entries
        const entries: StudentEntry[] = parsed
          .map((row) => {
            const entry: StudentEntry = {
              email: row.email?.trim(),
              username: row.username?.trim(),
              status: "pending",
            };

            // Validation
            if (!entry.email && !entry.username) {
              entry.status = "failed";
              entry.error = "Either email or username required";
            } else if (
              entry.email &&
              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry.email)
            ) {
              entry.status = "failed";
              entry.error = "Invalid email format";
            } else if (
              entry.username &&
              entry.username.length < 3
            ) {
              entry.status = "failed";
              entry.error = "Username must be at least 3 characters";
            }

            return entry;
          })
          .filter((entry) => entry.email || entry.username); // Remove completely empty rows

        if (entries.length > 100) {
          alert("Maximum 100 students per import. Please split your file.");
          return;
        }

        setStudents(entries);
      },
      error: (error) => {
        alert(`Error parsing CSV: ${error.message}`);
      },
    });
  };

  const handleImport = async () => {
    if (students.length === 0) return;

    // Filter out already failed entries
    const validStudents = students.filter((s) => s.status !== "failed");

    if (validStudents.length === 0) {
      alert("No valid students to import");
      return;
    }

    setImporting(true);

    try {
      const result = await api.bulkInviteStudents(
        classroomId,
        validStudents.map((s) => ({
          email: s.email,
          username: s.username,
        })),
      );

      setResults(result.results);
      setCompleted(true);

      if (onComplete) {
        onComplete();
      }
    } catch (error: any) {
      alert(`Error importing students: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "email,username\nstudent1@example.com,student1\nstudent2@example.com,\n,student3";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_invite_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadFailedEntries = () => {
    const failed = results.filter((r) => r.status === "failed");
    if (failed.length === 0) return;

    const csv = [
      "email,username,error",
      ...failed.map(
        (r) => `${r.email || ""},${r.username || ""},${r.error || ""}`,
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "failed_invitations.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const successCount = completed
    ? results.filter((r) => r.status === "success").length
    : students.filter((s) => s.status === "pending").length;
  const failedCount = completed
    ? results.filter((r) => r.status === "failed").length
    : students.filter((s) => s.status === "failed").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Invite Students</DialogTitle>
          <DialogDescription>
            Upload a CSV file to invite multiple students at once.{" "}
            <Button
              variant="link"
              size="sm"
              className="p-0 h-auto"
              onClick={downloadTemplate}
            >
              Download template
            </Button>
          </DialogDescription>
        </DialogHeader>

        {!completed && students.length === 0 && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                CSV must have columns: <code>email</code> and/or{" "}
                <code>username</code>. Each row must have at least one of these
                fields.
              </AlertDescription>
            </Alert>

            <div className="flex items-center justify-center border-2 border-dashed rounded-lg p-12">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Click to upload CSV
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Maximum 100 students per import
                  </span>
                </div>
              </label>
            </div>
          </div>
        )}

        {students.length > 0 && !completed && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">{successCount}</span> valid,{" "}
                <span className="font-medium text-destructive">
                  {failedCount}
                </span>{" "}
                invalid
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStudents([]);
                  setResults([]);
                }}
              >
                Clear
              </Button>
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {student.status === "pending" && (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        )}
                        {student.status === "failed" && (
                          <XCircle className="w-4 h-4 text-destructive" />
                        )}
                      </TableCell>
                      <TableCell>{student.email || "-"}</TableCell>
                      <TableCell>{student.username || "-"}</TableCell>
                      <TableCell className="text-xs text-destructive">
                        {student.error}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {completed && (
          <div className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <AlertDescription>
                Import completed: {successCount} successful, {failedCount}{" "}
                failed
              </AlertDescription>
            </Alert>

            {failedCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={downloadFailedEntries}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Failed Entries
              </Button>
            )}

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((result, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {result.status === "success" && (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        )}
                        {result.status === "failed" && (
                          <XCircle className="w-4 h-4 text-destructive" />
                        )}
                      </TableCell>
                      <TableCell>{result.email || "-"}</TableCell>
                      <TableCell>{result.username || "-"}</TableCell>
                      <TableCell className="text-xs text-destructive">
                        {result.error}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          {!completed && students.length > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={importing || successCount === 0}>
                {importing ? "Importing..." : `Import ${successCount} Students`}
              </Button>
            </>
          )}

          {completed && (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
